// Validadores e normalizadores compartilhados entre API e apps.

/** Remove tudo que não for dígito. */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** Valida os dígitos verificadores de um CPF. Aceita com ou sem máscara. */
export function isValidCpf(value: string): boolean {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  const digit = (slice: string, factor: number) => {
    let total = 0;
    for (const char of slice) total += Number(char) * factor--;
    const rest = (total * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return digit(cpf.slice(0, 9), 10) === Number(cpf[9]) && digit(cpf.slice(0, 10), 11) === Number(cpf[10]);
}

/** `12345678909` → `***.456.789-**` (LGPD: listas e logs nunca exibem o CPF completo). */
export function maskCpf(value: string | null | undefined): string | null {
  if (!value) return null;
  const cpf = onlyDigits(value);
  if (cpf.length !== 11) return "***.***.***-**";
  return `***.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-**`;
}

/** `12345678909` → `123.456.789-09` (só para o detalhe, com `team:manage`). */
export function formatCpf(value: string | null | undefined): string | null {
  if (!value) return null;
  const cpf = onlyDigits(value);
  if (cpf.length !== 11) return value;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

/**
 * Normaliza um telefone brasileiro para E.164 (`+55DDDNÚMERO`).
 * Aceita 10 ou 11 dígitos (com DDD), com ou sem o prefixo 55 e o `+`.
 * Retorna `null` quando o valor não é um celular/fixo válido.
 */
export function normalizeBrazilianPhone(value: string | null | undefined): string | null {
  if (!value) return null;
  let digits = onlyDigits(value);
  if (digits.startsWith("0") && digits.length > 11) digits = digits.replace(/^0+/, "");
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) digits = digits.slice(2);
  if (digits.length !== 10 && digits.length !== 11) return null;
  const ddd = Number(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) return null;
  if (digits.length === 11 && digits[2] !== "9") return null;
  return `+55${digits}`;
}

/** `+5511987654321` → `(11) 98765-4321`. */
export function formatBrazilianPhone(value: string | null | undefined): string {
  if (!value) return "";
  const digits = onlyDigits(value).replace(/^55/, "");
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return value;
}

/** "Maria da Silva Souza" → "M. S." (painéis e listas usam iniciais, 5.10). */
export function patientInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]! : "";
  return last ? `${first[0]!.toUpperCase()}. ${last[0]!.toUpperCase()}.` : `${first[0]!.toUpperCase()}.`;
}

/** Nome de exibição padrão: primeiro + último nome (4.3). */
export function defaultDisplayName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts[0]} ${parts[parts.length - 1]}`;
}
